import { useState } from 'react';
import { prefersReducedMotion } from './mediaQueries';
import { useTranslation } from 'react-i18next';
import type {
  AssistantFeedback,
  AssistantFeedbackReason,
} from '@/api/assistant/assistantApi.types';
import { Icon } from '@/components/common/Icon';
import { useAssistantFeedbackMutation } from '@/hooks/useAssistant';
import { cn } from '@/lib/utils';

const FEEDBACK_REASONS: AssistantFeedbackReason[] = [
  'wrong_answer',
  'out_of_date',
  'not_what_i_asked',
  'too_long',
];

interface AssistantFeedbackButtonsProps {
  messageId: string;
  feedback: AssistantFeedback | null;
}

function scrollReasonsIntoView(element: HTMLDivElement | null) {
  if (element === null) {
    return;
  }
  element.scrollIntoView({
    block: 'nearest',
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
}

export function AssistantFeedbackButtons({ messageId, feedback }: AssistantFeedbackButtonsProps) {
  const { t } = useTranslation('assistant');
  const feedbackMutation = useAssistantFeedbackMutation();
  const [isChoosingReason, setIsChoosingReason] = useState(false);

  function sendFeedback(nextFeedback: AssistantFeedback, reason: AssistantFeedbackReason | null) {
    feedbackMutation.mutate({ messageId, feedback: nextFeedback, reason });
    setIsChoosingReason(false);
  }

  if (feedback !== null) {
    return (
      <p className="mt-2 text-[11px] text-zoosuccesstext font-semibold flex items-center gap-1">
        <Icon name="Check" />
        {t('feedback.thanks')}
      </p>
    );
  }
  if (isChoosingReason) {
    return (
      <div className="mt-2" ref={scrollReasonsIntoView}>
        <p className="text-[11px] text-zoomuted mb-1.5">{t('feedback.whatWentWrong')}</p>
        <div className="flex flex-wrap gap-1.5">
          {FEEDBACK_REASONS.map(function renderReason(reason) {
            return (
              <button
                key={reason}
                type="button"
                onClick={function choose() {
                  sendFeedback('not_helpful', reason);
                }}
                className="px-2.5 py-1 text-[11px] rounded-lg border border-zooborder text-zooindigo bg-zoowhite hover:border-zooprimary hover:text-zooprimary"
              >
                {t('feedback.reasons.' + reason)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  const buttonClassName =
    'w-7 h-7 rounded-lg flex items-center justify-center text-zoomuted hover:bg-zoovpalelavender hover:text-zooindigo';
  return (
    <div className="flex items-center gap-0.5 mt-1.5">
      <button
        type="button"
        title={t('feedback.helpful')}
        aria-label={t('feedback.helpful')}
        className={cn(buttonClassName)}
        onClick={function markHelpful() {
          sendFeedback('helpful', null);
        }}
      >
        <Icon name="ThumbsUp" />
      </button>
      <button
        type="button"
        title={t('feedback.notHelpful')}
        aria-label={t('feedback.notHelpful')}
        className={cn(buttonClassName)}
        onClick={function chooseReason() {
          setIsChoosingReason(true);
        }}
      >
        <Icon name="ThumbsDown" />
      </button>
    </div>
  );
}
